import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../shared/Layout";
import Table from "../widgets/Table";
import Pagination from "../widgets/Pagination";
import PageTitle from "../widgets/PageTitle";
import { Bars } from "react-loader-spinner";
import { getTransactions, getAssets, getChains } from "../services/InventoryService";
import { getAccounts } from "../services/HoldingService";
import { transactionColumns } from "../data/TransactionTableData";
import transactionModel from "../models/TransactionModel";
import DropDownFilter from "../components/Filters/DropDownFilter";
import RangeFilter from "../components/Filters/RangeFilter";
import DocumentSort from "../components/Sorts/DocumentSort";
import { updatePage } from "../services/CommonService";
import { getExchangeConfig } from "../helper/platformConfig";
import { findAssetIdsBySymbol, buildSymbolDropdownOptions } from "../helper/formatAsset";
import moment from "moment";

const Transactions = () => {
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  const [accountOptions, setAccountOptions] = useState([]);
  const [assetOptions, setAssetOptions] = useState([]);
  const [assetsMap, setAssetsMap] = useState(new Map());
  const [chainsMap, setChainsMap] = useState(new Map());
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [account, setAccount] = useState(null);
  // Asset filter source of truth; dropdown value derived in symbolSelectedValue.
  const [assetIds, setAssetIds] = useState(null);
  // Suppresses fetch when ?symbol=X has zero matches (prevents cross-asset leak).
  const [symbolNotFound, setSymbolNotFound] = useState(false);

  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [sortType, setSortType] = useState({ key: "timestamp", order: "desc" });

  const rowsPerPage = 10;

  // Load lookup data once on mount.
  useEffect(() => {
    Promise.all([getAccounts(), getAssets(), getChains()]).then(
      ([accountsResult, assets, chains]) => {
        if (accountsResult.status) {
          setAccountOptions(
            accountsResult.data.map((acc) => ({
              label: acc.name,
              value: Number(acc.id),
            }))
          );
        }

        const chMap = new Map(chains.map((c) => [c.id, c]));
        const asMap = new Map(assets.map((a) => [a.id, a]));
        setChainsMap(chMap);
        setAssetsMap(asMap);
        setAssetOptions(buildSymbolDropdownOptions(assets, chMap));
        setFiltersLoaded(true);
      }
    );
  }, []);

  // Sync filter state from URL params; re-runs on URL change so nav-back resets state.
  useEffect(() => {
    if (!filtersLoaded) return;

    const paramAccount = searchParams.get("account");
    setAccount(paramAccount ? Number(paramAccount) : null);

    const paramSymbol = searchParams.get("symbol");
    if (paramSymbol) {
      const matchingIds = findAssetIdsBySymbol(paramSymbol, Array.from(assetsMap.values()));
      if (matchingIds.length > 0) {
        setAssetIds(matchingIds);
        setSymbolNotFound(false);
        setErrorMsg("");
      } else {
        setAssetIds(null);
        setSymbolNotFound(true);
        setErrorMsg(`No assets matching '${paramSymbol}' found in your assets.`);
        setLoading(false);
      }
    } else {
      setAssetIds(null);
      setSymbolNotFound(false);
      setErrorMsg("");
    }
    setCurrentPage(0);
  }, [searchParams, filtersLoaded, assetsMap]);

  // Fetch transactions
  useEffect(() => {
    if (!filtersLoaded) return;
    if (symbolNotFound) return;
    fetchTransactions();
  }, [currentPage, account, assetIds, dateFrom, dateTo, sortType, filtersLoaded, symbolNotFound]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTransactions = () => {
    setLoading(true);
    setErrorMsg("");

    getTransactions(
      account,
      assetIds,
      dateFrom,
      dateTo,
      rowsPerPage,
      currentPage * rowsPerPage,
      sortType
    ).then((result) => {
      if (result.status) {
        const parsed = transactionModel.parseList(result.data, assetsMap, chainsMap);
        setData(parsed);
        setTotalPages(Math.ceil(result.totalRows / rowsPerPage));
        if (parsed.length === 0) {
          setErrorMsg("No Data Available");
        }
      } else {
        setData([]);
        setErrorMsg(result.message || "No Data Available");
      }
      setLoading(false);
    });
  };

  const onAccountChange = (value) => {
    if (loading) return;
    setCurrentPage(0);
    setAccount(value);
  };

  // Dropdown emits number (per-chain), "symbol:X" (all chains), or null.
  const onAssetChange = (value) => {
    if (loading) return;
    setCurrentPage(0);
    setSymbolNotFound(false);
    setErrorMsg("");
    if (value === null || value === undefined) {
      setAssetIds(null);
      return;
    }
    if (typeof value === "string" && value.startsWith("symbol:")) {
      const symbol = value.slice("symbol:".length);
      setAssetIds(findAssetIdsBySymbol(symbol, Array.from(assetsMap.values())));
      return;
    }
    const n = Number(value);
    setAssetIds(Number.isFinite(n) ? [n] : null);
  };

  // Reverse-derive dropdown value: 1 id → that id; N ids → "symbol:X" marker.
  const symbolSelectedValue = (() => {
    if (!assetIds || assetIds.length === 0) return null;
    if (assetIds.length === 1) return assetIds[0];
    const firstAsset = assetsMap.get(assetIds[0]);
    return firstAsset ? `symbol:${firstAsset["asset-token"]}` : null;
  })();

  const onDateChange = (_, value) => {
    if (loading) return;
    if (!value || !value[0] || !value[1]) {
      setDateFrom(null);
      setDateTo(null);
      return;
    }
    setDateFrom(moment(value[0]).format("YYYY-MM-DD"));
    setDateTo(moment(value[1]).format("YYYY-MM-DD"));
    setCurrentPage(0);
  };

  const onSortChange = (_, selectedOption) => {
    if (loading) return;
    setCurrentPage(0);
    setSortType(selectedOption.value);
  };

  return (
    <Layout className="space-y-6">
      <div className="relative lg:flex xl:flex items-center justify-between gap-6">
        <PageTitle title={getExchangeConfig().sidebarLabels[3]} level={1} />
        <div className="relative flex flex-col lg:flex-row items-center xl:justify-end lg:justify-end gap-6 mt-10 xl:mt-0 lg:mt-0">
          <DropDownFilter
            label="Account"
            data={accountOptions}
            selectedValue={account}
            onFilterChange={onAccountChange}
          />
          <DropDownFilter
            label="Symbol"
            data={assetOptions}
            selectedValue={symbolSelectedValue}
            onFilterChange={onAssetChange}
          />
          <RangeFilter onFilterchange={onDateChange} />
          <DocumentSort
            data={[
              { label: "Timestamp (Newest)", value: { key: "timestamp", order: "desc" } },
              { label: "Timestamp (Oldest)", value: { key: "timestamp", order: "asc" } },
            ]}
            onFilterchange={onSortChange}
          />
        </div>
      </div>

      {loading && (
        <div className="text-center py-10 flex justify-center">
          <Bars
            height="80"
            width="80"
            color="rgb(78,183,63)"
            ariaLabel="bars-loading"
            visible={true}
          />
        </div>
      )}

      {!loading && data.length > 0 && (
        <Table columns={transactionColumns} data={data} />
      )}

      {!loading && data.length === 0 && (
        <div className="text-center py-10">{errorMsg}</div>
      )}

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPage={totalPages}
          onPageChange={(type) => {
            if (loading) return;
            setCurrentPage((prevPage) => updatePage(prevPage, totalPages, type));
          }}
        />
      )}
    </Layout>
  );
};

export default Transactions;
